import axios, { isAxiosError } from 'axios';
import { type ValidRouteName, route } from 'ziggy-js';
import { usePage } from '@inertiajs/vue3';
import type {
	Timesheet,
	TimesheetContractOption,
} from '@/Pages/Timesheet/composables/types';
import type { Choice, ID, Maybe, Nullable } from '@jagaad/utils';

/** Workspaces that can manage talents, and therefore extract skills from a CV. */
export type TalentRouteNamePrefix = 'admin' | 'vendor';

const SKILL_EXTRACTION_TIMEOUT = 35000;

async function postApi(
	routeName: ValidRouteName,
	data: unknown,
	routeParams = {},
) {
	try {
		const response = await axios.post(route(routeName, routeParams), data);

		return response.data;
	} catch (e) {
		handleApiError(e);
	}
}

async function patchApi(
	routeName: ValidRouteName,
	data: unknown,
	routeParams = {},
) {
	try {
		const response = await axios.patch(route(routeName, routeParams), data);

		return response.data;
	} catch (e) {
		handleApiError(e);
	}
}

async function postSearchApi(
	routeName: ValidRouteName,
	query: unknown,
	routeParams = {},
	trashed = false,
) {
	return postApi(
		routeName,
		{
			query: query,
			trashed: trashed,
		},
		routeParams,
	);
}

async function getApi(routeName: ValidRouteName, routeParams = {}) {
	try {
		const response = await axios.get(route(routeName, routeParams ?? {}));
		return response.data;
	} catch (e) {
		handleApiError(e);
	}
}

async function deleteApi(routeName: ValidRouteName, routeParams = {}) {
	try {
		const response = await axios.delete(route(routeName, routeParams ?? {}));
		return response.data;
	} catch (e) {
		handleApiError(e);
	}
}

function handleApiError(e: unknown) {
	const page = usePage();

	const banner = isAxiosError<{ errors: string; message: string }>(e)
		? (e.response?.data?.errors ?? e.response?.data?.message ?? e.message)
		: String(e);

	page.props.jetstream.flash = {
		banner,
		bannerStyle: 'danger',
	};

	throw e;
}

function handleApiSuccess(message: string) {
	usePage().props.jetstream.flash = {
		banner: message,
		bannerStyle: 'success',
	};
}

async function resentTalentResetPasswordNotification(talentId: ID) {
	try {
		const response = await axios.get(
			route('admin.talents.password-reset', { talent: talentId }),
		);
		return response.data;
	} catch (e) {
		handleApiError(e);
	}
}

export default {
	handleApiSuccess,
	async fetchCountries(query: unknown) {
		return await postSearchApi('api.search.countries', query);
	},
	async fetchCurrencies(query: unknown) {
		return await postSearchApi('api.search.currencies', query);
	},
	async fetchStripeTaxCode(taxCode: string) {
		// @ts-expect-error no such route
		return await getApi('api.tax-codes.show.stripe', { taxCode: taxCode });
	},
	async fetchStripeTaxRate(taxRate: string) {
		// @ts-expect-error no such route
		return await getApi('api.tax-rates.show.stripe', { taxRate: taxRate });
	},
	async fetchUsers(query: unknown) {
		return await postSearchApi('api.search.users', query);
	},
	fetchBanks: async function (query: unknown) {
		return await postSearchApi('admin.api.banking.banks.search', query);
	},
	async checkPromoCode(promoCode: string, email: string) {
		return await postApi('api.check-promo-code', {
			promo: promoCode,
			email: email,
		});
	},
	async deleteFile(fileId: ID) {
		return await deleteApi('api.media.delete', { media: fileId });
	},
	async fetchProjectsByClientId(query: unknown) {
		return await getApi('api.propose.resources.projects', { clientId: query });
	},
	async fetchPositionsByProjectId(query: unknown, forContract?: boolean) {
		return await getApi('api.propose.resources.positions', {
			projectId: query,
			forContract: forContract,
		});
	},
	async updateNdaStatus(
		vendorId: ID,
		mediaId: ID,
		{ newStatus, statusNote = '' }: { newStatus: ID; statusNote?: string },
	) {
		return await patchApi(
			'api.vendors.nda.updateStatus',
			{ status: newStatus, status_note: statusNote },
			{ vendor: vendorId, media: mediaId },
		);
	},
	async fetchProjectProposedTalentsStatistics(
		projectId: ID,
		positionId: ID | null,
	) {
		return await getApi('api.propose.resources.statistics', {
			projectId,
			positionId,
		});
	},
	async fetchApplicationStatistics(positionId: Nullable<ID>) {
		return await getApi('api.applications.statistics', {
			positionId,
		});
	},
	async fetchClientStatistics(positionId: Nullable<ID>, clientId: Maybe<ID>) {
		return await getApi('api.clients.statistics', {
			positionId,
			client: clientId,
		});
	},
	async generateAnonymousCv(vendorResource: ID) {
		return await getApi('api.professionals.generate-anonymous-cv', {
			vendorResource,
		});
	},
	async downloadAnonymousCv(vendorResource: ID) {
		try {
			return await getApi('api.professionals.download-anonymous-cv', {
				vendorResource,
			});
		} catch (error) {
			console.error('Failed to download anonymous CV:', error);
			throw error;
		}
	},
	async regenerateAnonymousCvAfterEdit(vendorResource: ID) {
		try {
			await this.generateAnonymousCv(vendorResource);
			return await this.downloadAnonymousCv(vendorResource);
		} catch (error) {
			console.error('Failed to regenerate anonymous CV after edit:', error);
			throw error;
		}
	},
	async fetchStaticData() {
		return await getApi('static-data');
	},
	async fetchRolesAndSkills() {
		return await getApi('roles-and-skills');
	},
	async fetchContractsByClientId(
		client: unknown,
		vendor: unknown,
		paymentType: unknown,
	) {
		return await getApi('api.propose.resources.client-contracts', {
			paymentType: paymentType,
			clientId: client,
			vendorId: vendor,
		});
	},

	async fetchVendors(): Promise<Choice[]> {
		return await getApi('api.timesheet-resources.vendors');
	},

	async fetchContractAssignments(
		contract_id?: ID,
		vendor_id?: ID,
		selected_assignment_id?: ID,
	) {
		return await getApi('api.timesheet-resources.contract-assignments', {
			contract_id: contract_id,
			vendor_id: vendor_id,
			selected_assignment_id,
		});
	},

	async fetchTalentTimesheetEntriesByContractAssignmentIdAndPeriod(
		contractAssignmentId: ID,
		startDate: string,
		endDate: string,
		talentId: ID,
	): Promise<Timesheet[]> {
		return await postApi('api.timesheet-resources.filter', {
			start: startDate,
			end: endDate,
			project_contract_id: contractAssignmentId,
			resource_id: talentId,
		});
	},

	async fetchContracts(
		vendor_id?: ID,
		selected_contract_id?: ID,
	): Promise<TimesheetContractOption[]> {
		return await getApi('api.timesheet-resources.contracts', {
			vendor_id,
			selected_contract_id,
		});
	},

	async resendTalentPasswordResetNotification(talentId: ID) {
		return await resentTalentResetPasswordNotification(talentId);
	},

	async extractSkillsFromCV(
		routeNamePrefix: TalentRouteNamePrefix,
		cvFile: File,
		roleIds: (number | string)[] = [],
		existingSkillIds: (number | string)[] = [],
	) {
		const formData = new FormData();
		formData.append('cv', cvFile);
		roleIds.forEach((id) => formData.append('role_ids[]', String(id)));
		existingSkillIds.forEach((id) =>
			formData.append('existing_skill_ids[]', String(id)),
		);
		const response = await axios.post(
			route(`${routeNamePrefix}.professionals.preload-talent-cv-data`),
			formData,
			{
				headers: { 'Content-Type': 'multipart/form-data' },
				timeout: SKILL_EXTRACTION_TIMEOUT,
			},
		);
		return response.data;
	},

	async extractSkillsFromStoredCV(
		routeNamePrefix: TalentRouteNamePrefix,
		talentId: ID,
		roleIds: (number | string)[] = [],
		existingSkillIds: (number | string)[] = [],
	) {
		const response = await axios.post(
			route(`${routeNamePrefix}.professionals.extract-cv-skills`, talentId),
			{ role_ids: roleIds, existing_skill_ids: existingSkillIds },
			{ timeout: SKILL_EXTRACTION_TIMEOUT },
		);
		return response.data;
	},
};
